import React, { useEffect, useState } from "react";
import {
  IconMinimize,
  IconPlus,
  IconDownload,
  IconEdit,
  IconTrash,
  IconCheck,
  IconPencil,
  IconX,
  IconDeviceFloppy,
} from "@tabler/icons-react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleFullScreen,
  toggleFinalizationStatus,
  setActiveApplicationTab,
} from "../../../state/reducers/applicationSlice";
import {
  addFinalizedlistingEntry,
  addShortlistingEntry,
  updateShortlistingEntry,
  finalizeUniversity,
} from "../../../services/admin.services";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { updateCustomer } from "../../../services/customer.services";
import { toast } from "react-toastify";

// Utility function to normalize header names (lowercase + trim)
const normalizeHeaderName = (header) => {
  if (typeof header === "string") return header.toLowerCase().trim();
  if (header == null) return "";
  return String(header).toLowerCase().trim();
};

const looksLikeDateKey = (key = "") =>
  /(date|deadline|start|end|issued|expiry)/i.test(key);

const formatMaybeDate = (key, value) => {
  if (!value) return "";
  if (!looksLikeDateKey(key)) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : formatDisplayDate(d);
};

const normalizeKey = (k) =>
  String(k)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

const normalizeRowKeys = (row) => {
  const out = {};
  Object.keys(row || {}).forEach((k) => {
    out[normalizeKey(k)] = row[k];
  });
  return out;
};

// Treat keys that look like dates (same idea as UniversityTable)
const isDateKey = (k = "") =>
  /(date|deadline|start|end|issued|expiry)/i.test(String(k));

// Format utilities
// Replace your existing formatDisplayDate with this:
// const formatDisplayDate = (dateString) => {
//   if (!dateString) return "";
//   try {
//     const d = isYMD(dateString)
//       ? (() => {
//           const [y, m, day] = dateString.split("-").map(Number);
//           return new Date(y, m - 1, day);
//         })()
//       : new Date(dateString);
//     if (isNaN(d)) return "";
//     return d.toLocaleDateString("en-GB", {
//       day: "2-digit",
//       month: "short",
//       year: "numeric",
//     });
//   } catch {
//     return "";
//   }
// };

// // Replace your existing formatBackendDate with this:
// const formatBackendDate = (dateString) => {
//   if (!dateString) return "";
//   if (isYMD(dateString)) return dateString;
//   const d = new Date(dateString);
//   if (isNaN(d)) return "";
//   return toYMDLocal(d);
// };

const isValidUrl = (value) => {
  return (
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"))
  );
};

const stripOrdinalsAndPunctuation = (s) =>
  String(s || "")
    .replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, "$1")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// --- NEW: find the first date-ish substring within arbitrary text ---
const extractFirstDateLikeToken = (raw) => {
  if (!raw) return "";

  const s = String(raw);

  // 1) ISO yyyy-mm-dd
  const iso = s.match(/\b\d{4}-\d{2}-\d{2}\b/);
  if (iso) return iso[0];

  // 2) Named month: "2nd Feb, 2026" / "Feb 2nd, 2026"
  const named1 = s.match(
    /\b\d{1,2}(st|nd|rd|th)?\s+[A-Za-z]{3,9}\s*,?\s*\d{4}\b/
  );
  if (named1) return named1[0];

  const named2 = s.match(
    /\b[A-Za-z]{3,9}\s+\d{1,2}(st|nd|rd|th)?\s*,?\s*\d{4}\b/
  );
  if (named2) return named2[0];

  // 3) Numeric dd/mm/yyyy or mm/dd/yyyy (or with -)
  const numeric = s.match(/\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/);
  if (numeric) return numeric[0];

  return ""; // none found
};

// Parse many human formats, returning a real Date or null
const parseFlexibleDate = (input) => {
  if (!input) return null;
  if (input instanceof Date && !isNaN(input)) return input;

  // If there is extra text, grab the date part first
  const token = extractFirstDateLikeToken(input) || String(input);
  let s = stripOrdinalsAndPunctuation(token);

  // ISO yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // Named months ("2 Feb 2026", "Feb 2 2026")
  let d = new Date(s);
  if (!isNaN(d)) return new Date(d.getFullYear(), d.getMonth(), d.getDate());

  // Numeric dd/mm/yyyy or mm/dd/yyyy (we assume dd/mm/yyyy by default)
  const m1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m1) {
    let day = Number(m1[1]);
    let month = Number(m1[2]);
    let year = Number(m1[3]);
    if (year < 100) year += 2000;
    return new Date(year, month - 1, day);
  }

  return null;
};

const isYMD = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toYMDLocal = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// For displaying as text (e.g., "02 Feb 2026")
const formatDisplayDate = (value) => {
  if (!value) return "";
  try {
    const d = isYMD(value)
      ? (() => {
          const [y, m, day] = value.split("-").map(Number);
          return new Date(y, m - 1, day);
        })()
      : parseFlexibleDate(value);

    if (!d || isNaN(d)) return String(value); // fall back to original text

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
};

// For <input type="date"> binding
const getFormattedDateForInput = (value) => {
  if (!value) return "";
  if (isYMD(value)) return value;
  const d = parseFlexibleDate(value);
  return d ? toYMDLocal(d) : "";
};

// For saving to backend
const formatBackendDate = (value) => {
  if (!value) return "";
  if (isYMD(value)) return value;
  const d = parseFlexibleDate(value);
  return d ? toYMDLocal(d) : "";
};
const renderRichText = (input) => {
  if (input == null) return "";

  // 1) Fix double-protocol and missing 'h' cases
  let text = String(input)
    .replace(/https?:\/\/https?:\/\//g, "https://")
    .replace(/(^|\s)tps:\/\//g, "$1https://");

  // 2) Extract unique URLs
  const urlRegex = /https?:\/\/[^\s]+/g;
  const urls = [...new Set(text.match(urlRegex) || [])];

  // 3) Build parts with <a> tags
  let parts = [text];
  urls.forEach((url, i) => {
    parts = parts.flatMap((chunk) =>
      typeof chunk === "string" && chunk.includes(url)
        ? chunk.split(url).flatMap((seg, j, arr) =>
            j < arr.length - 1
              ? [
                  seg,
                  <a
                    key={`${url}-${i}-${j}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    {url}
                  </a>,
                ]
              : [seg]
          )
        : [chunk]
    );
  });

  // 4) Now split each string sub-part by newline and interleave <br />
  const withBreaks = parts.flatMap((chunk, i) => {
    if (typeof chunk !== "string") return [chunk];
    const lines = chunk.split(/\r?\n/);
    return lines.flatMap((line, j) =>
      j < lines.length - 1 ? [line, <br key={`br-${i}-${j}`} />] : [line]
    );
  });

  return <>{withBreaks}</>;
};

const ShortlistedTable = ({ activeTab, fetchStudentDetails }) => {
  const dispatch = useDispatch();
  const admins = useSelector((state) => state.admin?.admins);
  const selectedStudent = useSelector(
    (state) => state.user?.userDetails || state.app?.selectedStudent
  );
  const isTableFullScreen = useSelector(
    (state) => state.application.isTableFullScreen
  );
  const location = useLocation();

  // State management
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [data, setData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [editableColumns, setEditableColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedManager, setSelectedManager] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusOptions] = useState([
    "Submitted",
    "Approved",
    "Rejected",
    "Pending",
    "Finalized",
  ]);
  const [tagOptions] = useState(["Ambitious", "Moderate", "Safe"]);
  const [proceed, setProceed] = useState("");

  const studentId = selectedStudent?.student_id;
  const assignedApplicationManagerId =
    selectedStudent.assigned_admins?.assigned_application_manager?.id || null;

  const currentApplicationTab = useSelector(
    (state) => state.application.activeApplicationTab || "finalizedUniversity"
  );

  // Helper functions
  const generateUniqueId = () =>
    Date.now() * 1000 + Math.floor(Math.random() * 1000);

  const getFormattedDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const parsedDate = new Date(dateString);
      return isNaN(parsedDate) ? "" : formatBackendDate(parsedDate);
    } catch {
      return "";
    }
  };

  const ensureStatusField = (rows) => {
    return rows.map((row) => {
      if (
        !row.hasOwnProperty("status") ||
        row.status === undefined ||
        row.status === null
      ) {
        return { ...row, status: "Pending" };
      }
      return row;
    });
  };

  const handleFetchStudentDetails = () => {
    if (typeof fetchStudentDetails === "function") {
      fetchStudentDetails();
    }

    setTimeout(() => {
      dispatch(setActiveApplicationTab(currentApplicationTab));
    }, 50);
  };

  // Toggle edit mode - only available for finalizedUniversity
  const toggleEditMode = () => {
    if (activeTab !== "finalizedUniversity") {
      return;
    }

    if (isEditMode) {
      handleSaveAllChanges();
    } else {
      setEditData(JSON.parse(JSON.stringify(data)));
      prepareEditableColumns();
    }
    setIsEditMode(!isEditMode);
  };

  const prepareEditableColumns = () => {
    const editable = columns
      .filter(
        (col) =>
          !["sr.no.", "Edit", "Delete"].includes(col.name) &&
          (normalizeHeaderName(col.name) === "loginid" ||
            normalizeHeaderName(col.name) === "login" ||
            normalizeHeaderName(col.name) === "password")
      )
      .map((col) => ({
        name: col.name,
        originalName: col.name,
        isEditing: false,
      }));
    setEditableColumns(editable);
  };

  // Handle cell value changes
  // const handleCellChange = (rowIndex, columnName, value) => {
  //   const updatedData = [...editData];
  //   const normalizedColumnName = normalizeHeaderName(columnName);
  //   updatedData[rowIndex][normalizedColumnName] = value;
  //   setEditData(updatedData);
  // };
  const handleCellChange = (rowId, columnName, value) => {
    const updated = [...editData];
    const normalizedColumnName = normalizeHeaderName(columnName);
    const idx = updated.findIndex((r) => r && r.id === rowId);
    if (idx !== -1) {
      updated[idx] = { ...updated[idx], [normalizedColumnName]: value };
      setEditData(updated);
    }
  };

  // Save all changes
  const handleSaveAllChanges = async () => {
    // Map 'login' to 'loginid' for backend before saving
    const mappedEditData = editData.map((row) => {
      const newRow = { ...row };

      // If there's a 'login' field, map it to 'loginid' for backend
      if (newRow.login !== undefined) {
        newRow.loginid = newRow.login;
        // Keep both fields to maintain compatibility
      }

      return newRow;
    });

    setData(mappedEditData);

    const columnOrderRow = data.find((row) => row.__columnOrder) || {
      __columnOrder: columns.map((col) => col.name),
    };

    try {
      if (activeTab === "finalizedUniversity") {
        const finalDataToSave = ensureStatusField([
          ...mappedEditData.filter(
            (row) => !row.__columnOrder && !row.__deletedColumns
          ),
          columnOrderRow,
        ]);
        await addFinalizedlistingEntry(studentId, finalDataToSave);
      } else {
        await addShortlistingEntry(studentId, [
          ...mappedEditData,
          columnOrderRow,
        ]);
      }
      handleFetchStudentDetails();
      toast.success("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes. Please try again.");
    }
  };

  const handleFinalizeClick = async () => {
    if (selectedStudent.is_finalised) {
      const confirmUnfinalize = window.confirm(
        "Are you sure you want to Unfinalize this University?"
      );
      if (!confirmUnfinalize) return;

      try {
        await finalizeUniversity(selectedStudent.student_id, false);
        dispatch(toggleFinalizationStatus(false));
        toast.success("University has been Unfinalized.");
        handleFetchStudentDetails();
      } catch (error) {
        console.error("Error unfinalizing university:", error);
        toast.error("Failed to unfinalize university.");
      }
    } else {
      setIsFinalizeModalOpen(true);
    }
  };

  const handleAssignAndFinalize = async () => {
    if (!selectedManager) {
      toast.error("Please select an Application Manager before finalizing.");
      return;
    }

    try {
      await updateCustomer(selectedStudent.customer_id, {
        assigned_application_manager: selectedManager,
      });

      await finalizeUniversity(selectedStudent.student_id, true);
      dispatch(toggleFinalizationStatus(true));

      toast.success(`University finalized successfully!`);
      setIsFinalizeModalOpen(false);
      handleFetchStudentDetails();
    } catch (err) {
      console.error("Error in finalizing university:", err);
      toast.error("Failed to finalize university. Please try again.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  const handleDownloadAsExcel = () => {
    if (data.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const filteredData = data.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    if (filteredData.length === 0) {
      toast.error("No university data to export.");
      return;
    }

    const exportColumns = columns
      .filter(
        (col) =>
          ![
            "sr.no.",
            "Edit",
            "Delete",
            "status",
            "Status",
            "tag",
            "Tag",
          ].includes(col.name)
      )
      .map((col) => col.name);

    const formattedData = filteredData.map((row, index) => {
      const formattedRow = {};

      if (!exportColumns.some((col) => normalizeHeaderName(col) === "sr.no.")) {
        formattedRow["sr.no."] = index + 1;
      }

      exportColumns.forEach((columnName) => {
        const normalizedKey = normalizeHeaderName(columnName);
        const value = row[normalizedKey] || "";

        if (typeof value === "string") {
          if (isValidUrl(value)) {
            formattedRow[columnName] = {
              f: `HYPERLINK("${value}","${value}")`,
              v: `${value}`,
              l: { Target: value },
            };
          } else {
            formattedRow[columnName] = value;
          }
        } else {
          formattedRow[columnName] = value;
        }
      });

      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    const columnWidths = [];
    columnWidths.push({ wch: 8 });

    exportColumns.forEach((columnName) => {
      const maxLength = Math.max(
        columnName.length,
        ...formattedData.map((row) => String(row[columnName] || "").length)
      );
      columnWidths.push({ wch: Math.min(Math.max(maxLength + 2, 15), 50) });
    });

    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    const sheetName =
      activeTab === "finalizedUniversity"
        ? "Finalized Universities"
        : "Shortlisted Universities";

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `${sheetName
      .toLowerCase()
      .replace(/\s+/g, "_")}_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename, { bookType: "xlsx" });

    toast.success(`Excel file downloaded successfully!`);
  };

  const toggleFullScreenMode = () => {
    dispatch(toggleFullScreen());
  };

  // Data loading and initialization
  useEffect(() => {
    if (!selectedStudent) {
      console.warn("selectedStudent is not available");
      return;
    }

    const rawDataset =
      activeTab === "finalizedUniversity"
        ? selectedStudent?.is_finalized_excel ?? []
        : selectedStudent?.shortlisting ?? [];

    const dataset = rawDataset.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    // Get column order and deleted columns
    const orderRowFromActive = rawDataset.find((row) => row?.__columnOrder);
    const orderRowFromShortlisting =
      selectedStudent?.shortlisting?.find((row) => row?.__columnOrder) || null;

    const deletedColumnRow = rawDataset.find((row) => row.__deletedColumns);
    const deletedColumns = deletedColumnRow?.__deletedColumns ?? [];

    // Base ordered headers
    let orderedHeaders = (
      orderRowFromActive?.__columnOrder ||
      orderRowFromShortlisting?.__columnOrder ||
      []
    ).map((key) => normalizeHeaderName(key));

    // Respect deletions
    orderedHeaders = orderedHeaders.filter(
      (key) =>
        !["edit", "delete", "sr.no", "tag"].includes(key) &&
        !deletedColumns.map((c) => normalizeHeaderName(c)).includes(key)
    );

    // Collect all keys visible in the dataset
    const allKeys = Array.from(
      new Set(
        dataset.flatMap((row) =>
          row ? Object.keys(row).map((k) => normalizeHeaderName(k)) : []
        )
      )
    ).filter((k) => !["edit", "delete", "sr.no"].includes(k));

    // Append any missing keys
    allKeys.forEach((key) => {
      if (
        !orderedHeaders.includes(key) &&
        !["__columnorder", "__deletedcolumns"].includes(key) &&
        !deletedColumns.map((c) => normalizeHeaderName(c)).includes(key)
      ) {
        orderedHeaders.push(key);
      }
    });

    orderedHeaders = [...new Set(orderedHeaders)];

    // Ensure "status" exists for finalized view if not deleted
    if (
      activeTab === "finalizedUniversity" &&
      !orderedHeaders.includes("status") &&
      !deletedColumns.map((c) => normalizeHeaderName(c)).includes("status")
    ) {
      orderedHeaders.unshift("status");
    }

    const newData = dataset.map((row) => {
      const idValue =
        row.id && !isNaN(Number(row.id)) ? Number(row.id) : generateUniqueId();

      // Normalize all keys in the row
      const normalizedRow = {};
      Object.keys(row).forEach((key) => {
        const normalizedKey = normalizeHeaderName(key);
        normalizedRow[normalizedKey] = row[key];
      });

      const rowWithId = {
        ...normalizedRow,
        id: idValue,
      };

      orderedHeaders.forEach((key) => {
        const normalizedKey = normalizeHeaderName(key);
        if (
          !(normalizedKey in rowWithId) ||
          rowWithId[normalizedKey] === null ||
          rowWithId[normalizedKey] === undefined
        ) {
          if (normalizedKey === "status") {
            rowWithId[normalizedKey] = "Pending";
          } else {
            rowWithId[normalizedKey] = "";
          }
        } else if (
          typeof rowWithId[normalizedKey] !== "string" &&
          normalizedKey !== "id"
        ) {
          rowWithId[normalizedKey] = String(rowWithId[normalizedKey]);
        }
      });

      return rowWithId;
    });

    // Add any metadata rows
    const metadataRows = rawDataset.filter(
      (row) => row.__columnOrder || row.__deletedColumns
    );
    const completeData = [...newData, ...metadataRows];

    const renderTextWithLinks = (text) => {
      if (typeof text !== "string") return text;

      let cleanedText = text
        .replace(/https?:\/\/https?:\/\//g, "https://")
        .replace(/(^|\s)tps:\/\//g, "$1https://");

      const urlRegex = /https?:\/\/[^\s]+/g;
      const matchedUrls = [...cleanedText.matchAll(urlRegex)].map((m) => m[0]);
      const uniqueUrls = [...new Set(matchedUrls)];

      let finalParts = [cleanedText];
      uniqueUrls.forEach((url) => {
        finalParts = finalParts.flatMap((part, i) =>
          typeof part === "string" && part.includes(url)
            ? part.split(url).flatMap((seg, j, arr) =>
                j < arr.length - 1
                  ? [
                      seg,
                      <a
                        key={`${url}-${i}-${j}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        {url}
                      </a>,
                    ]
                  : [seg]
              )
            : [part]
        );
      });

      return <>{finalParts}</>;
    };

    const priorityColumns = [];
    // if (orderedHeaders.includes("tag")) {
    //   priorityColumns.push("tag");
    // }
    if (orderedHeaders.includes("status")) {
      priorityColumns.push("status");
    }

    const prioritizedColumns = priorityColumns
      .filter((key) => orderedHeaders.includes(key))
      .map((key) => {
        const normalizedKey = normalizeHeaderName(key);
        const cell =
          normalizedKey === "status"
            ? (row) => (
                <span
                  className={`px-2 py-1 rounded-full text-white text-sm whitespace-nowrap ${
                    row.status === "Submitted"
                      ? "bg-blue-500"
                      : row.status === "Approved"
                      ? "bg-green-500"
                      : row.status === "Rejected"
                      ? "bg-red-500"
                      : row.status === "Pending"
                      ? "bg-orange-500 text-black"
                      : row.status === "Finalized"
                      ? "bg-purple-500"
                      : "bg-gray-400"
                  }`}
                >
                  {row.status}
                </span>
              )
            : normalizedKey === "tag"
            ? (row) => (
                <span
                  className={`px-2 py-1 rounded-full text-white text-sm whitespace-nowrap ${
                    row.tag === "Ambitious"
                      ? "bg-red-500"
                      : row.tag === "Moderate"
                      ? "bg-yellow-500 text-black"
                      : row.tag === "Safe"
                      ? "bg-green-500"
                      : "bg-gray-400"
                  }`}
                >
                  {row.tag}
                </span>
              )
            : (row) => (
                <div className="text-left">
                  {renderTextWithLinks(row[normalizedKey])}
                </div>
              );

        return {
          name: key,
          selector: (row) => row[normalizedKey] || "",
          // center: true,
          // width: "150px",
          center: true,
          grow: 1, // ⬅️ lets it take leftover width
          minWidth: "140px", // ⬅️ keeps readability
          cell,
        };
      });

    const excludedColumns = ["tag", "status", "sr.no.", "id"];
    const dynamicColumns = orderedHeaders
      .filter((key) => !excludedColumns.includes(normalizeHeaderName(key)))
      .filter(
        (key) =>
          !deletedColumns.map((c) => normalizeHeaderName(c)).includes(key)
      )
      .map((key) => {
        const normalizedKey = normalizeHeaderName(key);
        return {
          name: key,
          selector: (row) => row[normalizedKey] || "",
          // center: true,
          // width: "200px",
          center: true,
          grow: 1,
          minWidth: "160px",
          // cell: (row) => (
          //   // <div
          //   //   className="text-left p-2 break-words"
          //   //   style={{
          //   //     wordWrap: "break-word",
          //   //     overflowWrap: "anywhere",
          //   //     whiteSpace: "normal",
          //   //   }}
          //   // >
          //   //   {renderTextWithLinks(row[normalizedKey] || "")}
          //   // </div>
          //   <div
          //     style={{
          //       whiteSpace: "pre-wrap",
          //       overflowWrap: "anywhere",
          //       textAlign: "left",
          //       lineHeight: 1.35,
          //     }}
          //   >
          //     {renderRichText(row[normalizedKey] || "")}
          //   </div>
          // ),
          cell: (row) => {
            // robust value fetch
            let value =
              row[normalizedKey] ?? row[key] ?? row[normalizeKey(key)] ?? "";

            // pretty print dates in view mode
            if (isDateKey(normalizedKey)) {
              value = formatDisplayDate(
                typeof value === "string" ? value : String(value ?? "")
              );
            }

            // always string-coerce for the linkifier
            const safeText =
              typeof value === "string" ? value : String(value ?? "");

            return (
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  textAlign: "left",
                  lineHeight: 1.35,
                }}
              >
                {renderRichText(safeText)}
              </div>
            );
          },
        };
      });

    const fixedColumns = [
      {
        name: "sr.no.",
        cell: (row, index) => index + 1,
        center: true,
        width: "80px",
      },
    ];

    const allColumns = [
      ...fixedColumns,
      ...prioritizedColumns,
      ...dynamicColumns,
    ];

    setColumns(allColumns);
    setData(completeData);
  }, [activeTab, selectedStudent]);

  // Generate editable columns for the DataTable
  const getEditableDataColumns = () => {
    if (!isEditMode || !editData) {
      return columns;
    }

    const filteredEditData = editData.filter(
      (row) => !row.__columnOrder && !row.__deletedColumns
    );

    const editableDataColumns = [
      {
        name: "sr.no.",
        cell: (row, index) => index + 1,
        center: true,
        width: "80px",
      },
    ];

    columns.forEach((col) => {
      if (col.name === "sr.no.") {
        return;
      }

      const normalizedColName = normalizeHeaderName(col.name);
      const isEditable =
        activeTab === "finalizedUniversity" &&
        (normalizedColName === "loginid" ||
          normalizedColName === "login" ||
          normalizedColName === "password");

      const columnDef = {
        name: col.name,
        selector: (row) => row[normalizedColName] || "",
        center: true,
        width: "150px",
        cell: (row, index) => {
          if (!isEditable) {
            if (normalizedColName === "status") {
              return (
                <span
                  className={`px-2 py-1 rounded-full text-white text-sm whitespace-nowrap ${
                    row.status === "Submitted"
                      ? "bg-blue-500"
                      : row.status === "Approved"
                      ? "bg-green-500"
                      : row.status === "Rejected"
                      ? "bg-red-500"
                      : row.status === "Pending"
                      ? "bg-orange-500 text-black"
                      : row.status === "Finalized"
                      ? "bg-purple-500"
                      : "bg-gray-400"
                  }`}
                >
                  {row.status}
                </span>
              );
            } else if (normalizedColName === "tag") {
              return (
                <span
                  className={`px-2 py-1 rounded-full text-white text-sm whitespace-nowrap ${
                    row.tag === "Ambitious"
                      ? "bg-red-500"
                      : row.tag === "Moderate"
                      ? "bg-yellow-500 text-black"
                      : row.tag === "Safe"
                      ? "bg-green-500"
                      : "bg-gray-400"
                  }`}
                >
                  {row.tag}
                </span>
              );
            } else {
              return <div>{row[normalizedColName] || ""}</div>;
            }
          }

          return (
            <input
              type="text"
              className="border border-gray-300 rounded px-2 py-1 w-full"
              value={row[normalizedColName] || ""}
              onChange={(e) =>
                // handleCellChange(index, normalizedColName, e.target.value)
                handleCellChange(row.id, normalizedColName, e.target.value)
              }
            />
          );
        },
      };

      editableDataColumns.push(columnDef);
    });

    return editableDataColumns;
  };

  // Table styling
  const customStyles = {
    table: {
      style: {
        borderCollapse: "separate",
        borderSpacing: 0,
        width: "100%",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        overflow: "hidden",
        tableLayout: "auto",
      },
    },
    headRow: {
      style: {
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        minHeight: "56px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#2563eb",
        color: "white",
        textAlign: "center",
        padding: "12px 8px",
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        minHeight: "48px",
      },
    },
    cells: {
      style: {
        // whiteSpace: "normal",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        textAlign: "center",
        padding: "8px",
        borderRight: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        fontSize: "14px",
        transition: "background-color 0.2s",
        minHeight: "48px",
        overflow: "hidden",
      },
    },
    rows: {
      style: {
        backgroundColor: "white",
        minHeight: "48px",
        "&:nth-of-type(odd)": {
          backgroundColor: "#f9fafb",
        },
        "&:hover": {
          backgroundColor: "#f3f4f6",
          cursor: "pointer",
        },
        transition: "background-color 0.2s",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f3f4f6",
        cursor: "pointer",
      },
    },
    pagination: {
      style: {
        borderTop: "none",
        marginTop: "10px",
        borderRadius: "0 0 8px 8px",
      },
      pageButtonsStyle: {
        color: "#2563eb",
        fill: "#2563eb",
        "&:disabled": {
          color: "#c3c5c8",
          fill: "#c3c5c8",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "#eff6ff",
        },
        "&:focus": {
          outline: "none",
        },
      },
    },
  };

  // Button styling classes
  const primaryButtonClass =
    "bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 flex items-center gap-2";
  const secondaryButtonClass =
    "bg-gray-100 text-gray-700 px-3 py-2 rounded shadow hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center gap-2";
  const dangerButtonClass =
    "bg-red-600 text-white px-3 py-2 rounded shadow hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 flex items-center gap-2";
  const successButtonClass =
    "bg-green-600 text-white px-3 py-2 rounded shadow hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-300 flex items-center gap-2";
  const warningButtonClass =
    "bg-yellow-500 text-white px-3 py-2 rounded shadow hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 flex items-center gap-2";
  const editModeButtonClass =
    "bg-purple-600 text-white px-3 py-2 rounded shadow hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 flex items-center gap-2";

  return (
    <>
      <div
        // className={`bg-white border border-blue-100 shadow-lg rounded-lg ${
        //   isTableFullScreen
        //     ? "fixed top-0 left-0 w-screen h-screen z-[100] overflow-hidden"
        //     : "relative w-full h-[660px] md:h-[620px] overflow-hidden"
        // }`}
        className={`bg-white border border-blue-100 shadow-lg rounded-lg ${
          isTableFullScreen
            ? "fixed inset-0 w-screen h-screen z-[100] overflow-hidden" // ⬅️ use inset-0
            : "relative w-full h-[660px] md:h-[620px] overflow-hidden"
        }`}
      >
        <div className="absolute left-0 w-full h-full px-6 py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {activeTab === "finalizedUniversity"
                ? "Finalized Universities"
                : "Shortlisted Universities"}
              {proceed && (
                <div className="text-red-500 text-sm font-medium mt-1">
                  {proceed}
                </div>
              )}
            </h2>
            <div className="flex flex-wrap gap-3">
              {activeTab === "finalizedUniversity" && (
                <button
                  className={
                    isEditMode ? successButtonClass : editModeButtonClass
                  }
                  onClick={toggleEditMode}
                >
                  {isEditMode ? (
                    <>
                      <IconDeviceFloppy size={18} />
                      Save Changes
                    </>
                  ) : (
                    <>
                      <IconPencil size={18} />
                      Edit LoginID/Password
                    </>
                  )}
                </button>
              )}

              <button
                className={successButtonClass}
                onClick={handleDownloadAsExcel}
              >
                <IconDownload size={18} />
                Download Excel
              </button>

              <button
                className={`${
                  isTableFullScreen ? warningButtonClass : secondaryButtonClass
                }`}
                onClick={toggleFullScreenMode}
              >
                <IconMinimize size={18} />
                {isTableFullScreen ? "Exit Full Screen" : "Full Screen"}
              </button>
            </div>
          </div>
          <div
            className={`${
              isTableFullScreen ? "h-[calc(100%-120px)]" : "h-[520px]"
            } transition-all duration-300 ease-in-out transform bg-white rounded-lg overflow-hidden`}
          >
            <div className="w-full h-full overflow-auto">
              <DataTable
                columns={isEditMode ? getEditableDataColumns() : columns}
                data={
                  isEditMode
                    ? (editData || []).filter(
                        (row) => !row.__columnOrder && !row.__deletedColumns
                      )
                    : data.filter(
                        (row) => !row.__columnOrder && !row.__deletedColumns
                      )
                }
                pagination={!isTableFullScreen}
                fixedHeader={true}
                fixedHeaderScrollHeight={
                  isTableFullScreen ? "calc(100vh - 180px)" : "460px"
                }
                responsive
                striped
                highlightOnHover
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationDefaultPage={currentPage}
                customStyles={customStyles}
                noDataComponent={
                  <div className="p-10 text-center text-gray-500">
                    No university data found.{" "}
                    {isEditMode
                      ? "Only Login/LoginID and Password fields can be edited."
                      : activeTab === "finalizedUniversity"
                      ? "Click 'Edit LoginID/Password' to modify credentials."
                      : ""}
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Finalize Modal */}
      {isFinalizeModalOpen && !selectedStudent.is_finalised && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-white p-6 shadow-xl rounded-lg w-full md:w-1/3 max-w-md">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Assign Application Manager
              </h2>
              <button
                onClick={() => setIsFinalizeModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <p className="mb-4 text-gray-600">
              Select an Application Manager before finalizing the university
              list:
            </p>

            <select
              className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
            >
              <option value="">-- Select Manager --</option>
              {admins &&
                admins.map((admin, index) => (
                  <option key={index} value={admin.admin_id}>
                    {admin.username}
                  </option>
                ))}
            </select>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => setIsFinalizeModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleAssignAndFinalize}
              >
                Assign & Finalize
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShortlistedTable;

// import React, { useEffect, useState } from "react";
// import {
//   IconMinimize,
//   IconPlus,
//   IconDownload,
//   IconEdit,
//   IconTrash,
//   IconCheck,
//   IconPencil,
//   IconX,
//   IconDeviceFloppy,
// } from "@tabler/icons-react";
// import DataTable from "react-data-table-component";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   toggleFullScreen,
//   toggleFinalizationStatus,
//   setActiveApplicationTab,
// } from "../../../state/reducers/applicationSlice";
// import {
//   addFinalizedlistingEntry,
//   addShortlistingEntry,
//   updateShortlistingEntry,
//   finalizeUniversity,
// } from "../../../services/admin.services";
// import { useLocation } from "react-router-dom";
// import * as XLSX from "xlsx";
// import { updateCustomer } from "../../../services/customer.services";
// import { toast } from "react-toastify";

// // Format utilities
// const formatDisplayDate = (dateString) => {
//   if (!dateString) return "";
//   const date = new Date(dateString);
//   return date.toLocaleDateString("en-GB", {
//     day: "2-digit",
//     month: "short",
//     year: "numeric",
//   });
// };

// const formatBackendDate = (dateString) => {
//   if (!dateString) return "";
//   const date = new Date(dateString);
//   return date.toISOString().split("T")[0]; // Convert to "YYYY-MM-DD" for backend
// };

// const isValidUrl = (value) => {
//   return (
//     typeof value === "string" &&
//     (value.startsWith("http://") || value.startsWith("https://"))
//   );
// };
// const ShortlistedTable = ({ activeTab, fetchStudentDetails }) => {
//   const dispatch = useDispatch();
//   const admins = useSelector((state) => state.admin?.admins);
//   const selectedStudent = useSelector(
//     (state) => state.user?.userDetails || state.app?.selectedStudent
//   );
//   const isTableFullScreen = useSelector(
//     (state) => state.application.isTableFullScreen
//   );
//   const location = useLocation();

//   // State management
//   const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [data, setData] = useState([]);
//   const [editData, setEditData] = useState(null);
//   const [columns, setColumns] = useState([]);
//   const [editableColumns, setEditableColumns] = useState([]);
//   const [selectedRows, setSelectedRows] = useState([]);
//   const [selectedManager, setSelectedManager] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(25);
//   const [statusOptions] = useState([
//     "Submitted",
//     "Approved",
//     "Rejected",
//     "Pending",
//     "Finalized",
//   ]);
//   const [tagOptions] = useState(["Ambitious", "Moderate", "Safe"]);
//   const [proceed, setProceed] = useState("");

//   const studentId = selectedStudent?.student_id;
//   const assignedApplicationManagerId =
//     selectedStudent.assigned_admins?.assigned_application_manager?.id || null;

//   const currentApplicationTab = useSelector(
//     (state) => state.application.activeApplicationTab || "finalizedUniversity"
//   ); // Helper functions
//   const generateUniqueId = () =>
//     Date.now() * 1000 + Math.floor(Math.random() * 1000);

//   const getFormattedDateForInput = (dateString) => {
//     if (!dateString) return "";
//     try {
//       const parsedDate = new Date(dateString);
//       return isNaN(parsedDate) ? "" : formatBackendDate(parsedDate);
//     } catch {
//       return "";
//     }
//   };

//   const ensureStatusField = (rows) => {
//     return rows.map((row) => {
//       if (
//         !row.hasOwnProperty("status") ||
//         row.status === undefined ||
//         row.status === null
//       ) {
//         return { ...row, status: "Pending" };
//       }
//       return row;
//     });
//   };

//   const handleFetchStudentDetails = () => {
//     // First call the original function if it exists
//     if (typeof fetchStudentDetails === "function") {
//       fetchStudentDetails();
//     }

//     // Then ensure we restore the active tab
//     setTimeout(() => {
//       dispatch(setActiveApplicationTab(currentApplicationTab));
//     }, 50);
//   };

//   // Toggle edit mode - only available for finalizedUniversity
//   const toggleEditMode = () => {
//     // Return early if not on finalizedUniversity tab
//     if (activeTab !== "finalizedUniversity") {
//       return;
//     }

//     if (isEditMode) {
//       // Save all changes when exiting edit mode
//       handleSaveAllChanges();
//     } else {
//       // Enter edit mode
//       setEditData(JSON.parse(JSON.stringify(data))); // Deep clone data
//       // Set up editable columns - only for LoginID and Password
//       prepareEditableColumns();
//     }
//     setIsEditMode(!isEditMode);
//   };

//   const prepareEditableColumns = () => {
//     // Only allow LoginID and Password fields to be edited
//     const editable = columns
//       .filter(
//         (col) =>
//           !["sr.no.", "Edit", "Delete"].includes(col.name) &&
//           (col.name.toLowerCase() === "loginid" ||
//             col.name.toLowerCase() === "password")
//       )
//       .map((col) => ({
//         name: col.name,
//         originalName: col.name,
//         isEditing: false,
//       }));
//     setEditableColumns(editable);
//   };

//   // Handle cell value changes
//   const handleCellChange = (rowIndex, columnName, value) => {
//     const updatedData = [...editData];
//     updatedData[rowIndex][columnName] = value;
//     setEditData(updatedData);
//   }; // Save all changes
//   const handleSaveAllChanges = async () => {
//     // Update data state with edited values
//     setData(editData);

//     // Create column order for saving
//     const columnOrderRow = data.find((row) => row.__columnOrder) || {
//       __columnOrder: columns.map((col) => col.name),
//     };

//     // Save changes to backend
//     try {
//       if (activeTab === "finalizedUniversity") {
//         const finalDataToSave = ensureStatusField([
//           ...editData.filter(
//             (row) => !row.__columnOrder && !row.__deletedColumns
//           ),
//           columnOrderRow,
//         ]);
//         await addFinalizedlistingEntry(studentId, finalDataToSave);
//       } else {
//         // This should not happen for shortlisting as edit mode is disabled
//         await addShortlistingEntry(studentId, [...editData, columnOrderRow]);
//       }
//       // Use the wrapper function instead of direct call
//       handleFetchStudentDetails();
//     } catch (error) {
//       console.error("Error saving changes:", error);
//       alert("Failed to save changes. Please try again.");
//     }
//   };

//   const handleFinalizeClick = async () => {
//     if (selectedStudent.is_finalised) {
//       const confirmUnfinalize = window.confirm(
//         "Are you sure you want to Unfinalize this University?"
//       );
//       if (!confirmUnfinalize) return;

//       try {
//         await finalizeUniversity(selectedStudent.student_id, false);
//         dispatch(toggleFinalizationStatus(false));
//         alert("University has been Unfinalized.");

//         // Call fetch function directly
//         handleFetchStudentDetails();
//       } catch (error) {
//         console.error("Error unfinalizing university:", error);
//         alert("Failed to unfinalize university.");
//       }
//     } else {
//       setIsFinalizeModalOpen(true);
//     }
//   };

//   const handleAssignAndFinalize = async () => {
//     if (!selectedManager) {
//       alert("Please select an Application Manager before finalizing.");
//       return;
//     }

//     try {
//       await updateCustomer(selectedStudent.customer_id, {
//         assigned_application_manager: selectedManager,
//       });

//       await finalizeUniversity(selectedStudent.student_id, true);
//       dispatch(toggleFinalizationStatus(true));

//       alert(`University finalized successfully!`);
//       setIsFinalizeModalOpen(false);

//       // Call fetch function directly
//       handleFetchStudentDetails();
//     } catch (err) {
//       console.error("Error in finalizing university:", err);
//       alert("Failed to finalize university. Please try again.");
//     }
//   };
//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const handleRowsPerPageChange = (newRowsPerPage) => {
//     setRowsPerPage(newRowsPerPage);
//     setCurrentPage(1);
//   };

//   const handleRowSelection = (id) => {
//     setSelectedRows((prevSelected) =>
//       prevSelected.includes(id)
//         ? prevSelected.filter((rowId) => rowId !== id)
//         : [...prevSelected, id]
//     );
//   };

//   const handleDownloadAsExcel = () => {
//     if (data.length === 0) {
//       alert("No data available to download.");
//       return;
//     }

//     // Get the filtered data (excluding metadata rows)
//     const filteredData = data.filter(
//       (row) => !row.__columnOrder && !row.__deletedColumns
//     );

//     if (filteredData.length === 0) {
//       alert("No university data to export.");
//       return;
//     }

//     // Get column headers in the same order as displayed in the table
//     // Exclude sr.no., Edit, Delete columns
//     const exportColumns = columns
//       .filter(
//         (col) =>
//           !["sr.no.", "Edit", "Delete", "status", "Status"].includes(col.name)
//       )
//       .map((col) => col.name);

//     // Create formatted data maintaining the same column order
//     // Create formatted data maintaining the same column order
//     const formattedData = filteredData.map((row, index) => {
//       const formattedRow = {};

//       // Add sr.no. only if it is not already in exportColumns
//       if (!exportColumns.some((col) => col.toLowerCase() === "sr.no.")) {
//         formattedRow["sr.no."] = index + 1;
//       }

//       // Add other columns in the same order as the table
//       exportColumns.forEach((columnName) => {
//         const key = columnName.toLowerCase(); // Consistent lowercase keys
//         const value = row[key] || "";

//         if (typeof value === "string") {
//           if (isValidUrl(value)) {
//             formattedRow[columnName] = {
//               f: `HYPERLINK("${value}","${value}")`,
//               v: `${value}`,
//               l: { Target: value },
//             };
//           } else {
//             formattedRow[columnName] = value;
//           }
//         } else {
//           formattedRow[columnName] = value;
//         }
//       });

//       return formattedRow;
//     });

//     // Convert to worksheet
//     const worksheet = XLSX.utils.json_to_sheet(formattedData);

//     // Set column widths based on content
//     const columnWidths = [];

//     // sr.no. column width
//     columnWidths.push({ wch: 8 });

//     // Calculate width for each column based on header and content
//     exportColumns.forEach((columnName) => {
//       const maxLength = Math.max(
//         columnName.length,
//         ...formattedData.map((row) => String(row[columnName] || "").length)
//       );
//       columnWidths.push({ wch: Math.min(Math.max(maxLength + 2, 15), 50) });
//     });

//     worksheet["!cols"] = columnWidths;

//     // Create workbook
//     const workbook = XLSX.utils.book_new();
//     const sheetName =
//       activeTab === "finalizedUniversity"
//         ? "Finalized Universities"
//         : "Shortlisted Universities";

//     XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

//     // Generate filename with timestamp
//     const timestamp = new Date().toISOString().split("T")[0];
//     const filename = `${sheetName
//       .toLowerCase()
//       .replace(/\s+/g, "_")}_${timestamp}.xlsx`;

//     // Download file - changed from .ods to .xlsx to match the first component
//     XLSX.writeFile(workbook, filename, { bookType: "xlsx" });

//     toast.success(`Excel file downloaded successfully!`);
//   };

//   const toggleFullScreenMode = () => {
//     dispatch(toggleFullScreen());
//   }; // Data loading and initialization
//   useEffect(() => {
//     if (!selectedStudent) {
//       console.warn("selectedStudent is not available");
//       return;
//     }

//     const rawDataset =
//       activeTab === "finalizedUniversity"
//         ? selectedStudent?.is_finalized_excel ?? []
//         : selectedStudent?.shortlisting ?? [];

//     const dataset = rawDataset.filter(
//       (row) => !row.__columnOrder && !row.__deletedColumns
//     );

//     // Always use column order from shortlisting
//     const shortlistingOrderRow = selectedStudent?.shortlisting?.find(
//       (row) => row.__columnOrder
//     );
//     const columnOrderFromShortlisting =
//       shortlistingOrderRow?.__columnOrder ?? [];

//     const deletedColumnRow = rawDataset.find((row) => row.__deletedColumns);
//     const deletedColumns = deletedColumnRow?.__deletedColumns ?? [];

//     // Collect all keys from current dataset
//     const allKeys = Array.from(
//       new Set(dataset.flatMap((row) => (row ? Object.keys(row) : [])))
//     ).filter((key) => !["edit", "delete", "sr.no"].includes(key.toLowerCase()));

//     // Final ordered headers = shortlisting order + any extras from dataset
//     let orderedHeaders = (shortlistingOrderRow?.__columnOrder || []).filter(
//       (key) =>
//         !["edit", "delete", "sr.no"].includes(key.toLowerCase()) &&
//         !deletedColumns.includes(key)
//     );

//     // Ensure missing keys like loginId/password are included even if __columnOrder missed them
//     allKeys.forEach((key) => {
//       if (
//         !orderedHeaders.includes(key) &&
//         !["__columnOrder", "__deletedColumns"].includes(key) &&
//         !deletedColumns.includes(key)
//       ) {
//         orderedHeaders.push(key);
//       }
//     });

//     // Ensure "status" is always included
//     if (
//       activeTab === "finalizedUniversity" &&
//       !orderedHeaders.includes("status")
//     ) {
//       orderedHeaders.unshift("status");
//     }

//     const newData = dataset.map((row) => {
//       const idValue =
//         row.id && !isNaN(Number(row.id)) ? Number(row.id) : generateUniqueId();

//       const rowWithId = {
//         ...row,
//         id: idValue,
//       };

//       orderedHeaders.forEach((key) => {
//         if (
//           !(key in rowWithId) ||
//           rowWithId[key] === null ||
//           rowWithId[key] === undefined
//         ) {
//           if (key.toLowerCase() === "status") {
//             rowWithId[key] = "Pending"; // Default value
//           } else {
//             rowWithId[key] = "";
//           }
//         } else if (typeof rowWithId[key] !== "string" && key !== "id") {
//           rowWithId[key] = String(rowWithId[key]);
//         }
//       });

//       return rowWithId;
//     });

//     // Add any metadata rows
//     const metadataRows = rawDataset.filter(
//       (row) => row.__columnOrder || row.__deletedColumns
//     );
//     const completeData = [...newData, ...metadataRows];

//     const renderTextWithLinks = (text) => {
//       if (typeof text !== "string") return text;

//       let cleanedText = text
//         .replace(/https?:\/\/https?:\/\//g, "https://")
//         .replace(/(^|\s)tps:\/\//g, "$1https://");

//       const urlRegex = /https?:\/\/[^\s]+/g;
//       const matchedUrls = [...cleanedText.matchAll(urlRegex)].map((m) => m[0]);
//       const uniqueUrls = [...new Set(matchedUrls)];

//       let finalParts = [cleanedText];
//       uniqueUrls.forEach((url) => {
//         finalParts = finalParts.flatMap((part, i) =>
//           typeof part === "string" && part.includes(url)
//             ? part.split(url).flatMap((seg, j, arr) =>
//                 j < arr.length - 1
//                   ? [
//                       seg,
//                       <a
//                         key={`${url}-${i}-${j}`}
//                         href={url}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                         className="text-blue-600 underline"
//                       >
//                         {url}
//                       </a>,
//                     ]
//                   : [seg]
//               )
//             : [part]
//         );
//       });

//       return <>{finalParts}</>;
//     };

//     const priorityColumns = [];
//     if (orderedHeaders.includes("tag")) {
//       priorityColumns.push("tag");
//     }

//     const prioritizedColumns = priorityColumns
//       .filter((key) => orderedHeaders.includes(key))
//       .map((key) => {
//         const cell =
//           key === "status"
//             ? (row) => (
//                 <span
//                   className={`px-2 py-1 rounded-full text-white text-sm ${
//                     row.status === "Submitted"
//                       ? "bg-blue-500"
//                       : row.status === "Approved"
//                       ? "bg-green-500"
//                       : row.status === "Rejected"
//                       ? "bg-red-500"
//                       : row.status === "Pending"
//                       ? "bg-orange-500 text-black"
//                       : row.status === "Finalized"
//                       ? "bg-purple-500"
//                       : "bg-gray-400"
//                   }`}
//                 >
//                   {row.status}
//                 </span>
//               )
//             : key === "tag"
//             ? (row) => (
//                 <span
//                   className={`px-2 py-1 rounded-full text-white text-sm ${
//                     row.tag === "Ambitious"
//                       ? "bg-red-500"
//                       : row.tag === "Moderate"
//                       ? "bg-yellow-500 text-black"
//                       : row.tag === "Safe"
//                       ? "bg-green-500"
//                       : "bg-gray-400"
//                   }`}
//                 >
//                   {row.tag}
//                 </span>
//               )
//             : (row) => (
//                 <div className="text-left">{renderTextWithLinks(row[key])}</div>
//               );

//         return {
//           name: key,
//           selector: (row) => row[key] || "",
//           center: true,
//           width: "150px",
//           cell,
//         };
//       });

//     const excludedColumns = ["tag", "status", "sr.no.", "id"];
//     const dynamicColumns = orderedHeaders
//       .filter((key) => !excludedColumns.includes(key))
//       .filter((key) => !deletedColumns.includes(key))
//       .map((key) => {
//         const lowerKey = key.toLowerCase();
//         return {
//           name: lowerKey, // Use lowercase for consistency
//           selector: (row) => row[lowerKey] || "",
//           center: true,
//           width: "200px",
//           cell: (row) => <div>{renderTextWithLinks(row[lowerKey] || "")}</div>,
//         };
//       });

//     const fixedColumns = [
//       {
//         name: "sr.no.",
//         cell: (row, index) => index + 1,
//         center: true,
//         width: "80px",
//       },
//     ];

//     const allColumns = [
//       ...fixedColumns,
//       ...prioritizedColumns,
//       ...dynamicColumns,
//     ];

//     setColumns(allColumns);
//     setData(completeData);
//   }, [activeTab, selectedStudent]); // Generate editable columns for the DataTable
//   const getEditableDataColumns = () => {
//     if (!isEditMode || !editData) {
//       return columns;
//     }

//     // We don't include the metadata rows in the DataTable view
//     const filteredEditData = editData.filter(
//       (row) => !row.__columnOrder && !row.__deletedColumns
//     );

//     const editableDataColumns = [
//       {
//         name: "sr.no.",
//         cell: (row, index) => index + 1,
//         center: true,
//         width: "80px",
//       },
//     ];

//     // Generate all columns but only make LoginID and Password editable
//     columns.forEach((col) => {
//       if (col.name === "sr.no.") {
//         return; // Skip, already added
//       }

//       const isEditable =
//         activeTab === "finalizedUniversity" &&
//         (col.name.toLowerCase() === "loginid" ||
//           col.name.toLowerCase() === "password");

//       const columnDef = {
//         name: col.name,
//         selector: (row) => row[col.name.toLowerCase()] || "",
//         center: true,
//         width: "150px",
//         cell: (row, index) => {
//           // Non-editable fields
//           if (!isEditable) {
//             if (col.name === "status") {
//               return (
//                 <span
//                   className={`px-2 py-1 rounded-full text-white text-sm ${
//                     row.status === "Submitted"
//                       ? "bg-blue-500"
//                       : row.status === "Approved"
//                       ? "bg-green-500"
//                       : row.status === "Rejected"
//                       ? "bg-red-500"
//                       : row.status === "Pending"
//                       ? "bg-orange-500 text-black"
//                       : row.status === "Finalized"
//                       ? "bg-purple-500"
//                       : "bg-gray-400"
//                   }`}
//                 >
//                   {row.status}
//                 </span>
//               );
//             } else if (col.name === "tag") {
//               return (
//                 <span
//                   className={`px-2 py-1 rounded-full text-white text-sm ${
//                     row.tag === "Ambitious"
//                       ? "bg-red-500"
//                       : row.tag === "Moderate"
//                       ? "bg-yellow-500 text-black"
//                       : row.tag === "Safe"
//                       ? "bg-green-500"
//                       : "bg-gray-400"
//                   }`}
//                 >
//                   {row.tag}
//                 </span>
//               );
//             } else {
//               // Standard non-editable field
//               return <div>{row[col.name] || ""}</div>;
//             }
//           }

//           // Editable fields (LoginID and Password)
//           return (
//             <input
//               type="text"
//               className="border border-gray-300 rounded px-2 py-1 w-full"
//               value={row[col.name.toLowerCase()] || ""}
//               onChange={(e) =>
//                 handleCellChange(index, col.name.toLowerCase(), e.target.value)
//               }
//             />
//           );
//         },
//       };

//       editableDataColumns.push(columnDef);
//     });

//     return editableDataColumns;
//   };

//   // Table styling
//   const customStyles = {
//     table: {
//       style: {
//         borderCollapse: "separate",
//         borderSpacing: 0,
//         width: "100%",
//         boxShadow:
//           "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
//         borderRadius: "8px",
//         overflow: "hidden",
//       },
//     },
//     headRow: {
//       style: {
//         borderTopLeftRadius: "8px",
//         borderTopRightRadius: "8px",
//       },
//     },
//     headCells: {
//       style: {
//         backgroundColor: "#2563eb",
//         color: "white",
//         padding: "12px 8px",
//         fontSize: "14px",
//         fontWeight: "600",
//         whiteSpace: "normal", // ✅ Allow wrapping
//         wordBreak: "break-word", // ✅ Break long words
//         overflowWrap: "anywhere", // ✅ Prevent ellipsis
//         textAlign: "center",
//       },
//     },
//     cells: {
//       style: {
//         whiteSpace: "normal",
//         wordWrap: "break-word",
//         textAlign: "center",
//         padding: "12px 8px",
//         borderRight: "1px solid #e5e7eb",
//         borderBottom: "1px solid #e5e7eb",
//         fontSize: "14px",
//         transition: "background-color 0.2s",
//       },
//     },
//     rows: {
//       style: {
//         backgroundColor: "white",
//         "&:nth-of-type(odd)": {
//           backgroundColor: "#f9fafb",
//         },
//         "&:hover": {
//           backgroundColor: "#f3f4f6",
//           cursor: "pointer",
//         },
//         transition: "background-color 0.2s",
//       },
//       highlightOnHoverStyle: {
//         backgroundColor: "#f3f4f6",
//         cursor: "pointer",
//       },
//     },
//     pagination: {
//       style: {
//         borderTop: "none",
//         marginTop: "10px",
//         borderRadius: "0 0 8px 8px",
//       },
//       pageButtonsStyle: {
//         color: "#2563eb",
//         fill: "#2563eb",
//         "&:disabled": {
//           color: "#c3c5c8",
//           fill: "#c3c5c8",
//         },
//         "&:hover:not(:disabled)": {
//           backgroundColor: "#eff6ff",
//         },
//         "&:focus": {
//           outline: "none",
//         },
//       },
//     },
//   };
//   // Button styling classes
//   const primaryButtonClass =
//     "bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 flex items-center gap-2";
//   const secondaryButtonClass =
//     "bg-gray-100 text-gray-700 px-3 py-2 rounded shadow hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center gap-2";
//   const dangerButtonClass =
//     "bg-red-600 text-white px-3 py-2 rounded shadow hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 flex items-center gap-2";
//   const successButtonClass =
//     "bg-green-600 text-white px-3 py-2 rounded shadow hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-300 flex items-center gap-2";
//   const warningButtonClass =
//     "bg-yellow-500 text-white px-3 py-2 rounded shadow hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 flex items-center gap-2";
//   const editModeButtonClass =
//     "bg-purple-600 text-white px-3 py-2 rounded shadow hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 flex items-center gap-2";

//   return (
//     <>
//       <div
//         className={`bg-white border border-blue-100 shadow-lg rounded-lg ${
//           isTableFullScreen
//             ? "fixed top-0 left-0 w-screen h-screen z-[100] overflow-hidden"
//             : "relative w-full h-[660px] md:h-[620px] overflow-hidden"
//         }`}
//       >
//         <div className="absolute left-0 w-full h-full px-6 py-6">
//           <div className="flex justify-between items-center mb-6">
//             <h2 className="text-xl font-semibold text-gray-800">
//               {activeTab === "finalizedUniversity"
//                 ? "Finalized Universities"
//                 : "Shortlisted Universities"}
//               {proceed && (
//                 <div className="text-red-500 text-sm font-medium mt-1">
//                   {proceed}
//                 </div>
//               )}
//             </h2>
//             <div className="flex flex-wrap gap-3">
//               {/* Only show Edit Mode toggle button for finalizedUniversity */}
//               {activeTab === "finalizedUniversity" && (
//                 <button
//                   className={
//                     isEditMode ? successButtonClass : editModeButtonClass
//                   }
//                   onClick={toggleEditMode}
//                 >
//                   {isEditMode ? (
//                     <>
//                       <IconDeviceFloppy size={18} />
//                       Save Changes
//                     </>
//                   ) : (
//                     <>
//                       <IconPencil size={18} />
//                       Edit LoginID/Password
//                     </>
//                   )}
//                 </button>
//               )}

//               {/* Non-edit mode buttons */}
//               {/* {!isEditMode && activeTab !== "finalizedUniversity" && (
//                 <button
//                   className={`${
//                     selectedStudent.is_finalised
//                       ? dangerButtonClass
//                       : successButtonClass
//                   }`}
//                   onClick={handleFinalizeClick}
//                 >
//                   {selectedStudent.is_finalised ? (
//                     <>
//                       <IconMinimize size={18} />
//                       Unfinalize University
//                     </>
//                   ) : (
//                     <>
//                       <IconCheck size={18} />
//                       Finalize University
//                     </>
//                   )}
//                 </button>
//               )} */}

//               <button
//                 className={successButtonClass}
//                 onClick={handleDownloadAsExcel}
//               >
//                 <IconDownload size={18} />
//                 Download Excel
//               </button>

//               <button
//                 className={`${
//                   isTableFullScreen ? warningButtonClass : secondaryButtonClass
//                 }`}
//                 onClick={toggleFullScreenMode}
//               >
//                 <IconMinimize size={18} />
//                 {isTableFullScreen ? "Exit Full Screen" : "Full Screen"}
//               </button>
//             </div>
//           </div>
//           <div
//             className={`${
//               isTableFullScreen ? "h-[calc(100%-120px)]" : "h-[520px]"
//             } transition-all duration-300 ease-in-out transform bg-white rounded-lg overflow-hidden`}
//           >
//             <div className="w-full h-full overflow-auto">
//               <DataTable
//                 columns={isEditMode ? getEditableDataColumns() : columns}
//                 data={
//                   isEditMode
//                     ? (editData || []).filter(
//                         (row) => !row.__columnOrder && !row.__deletedColumns
//                       )
//                     : data.filter(
//                         (row) => !row.__columnOrder && !row.__deletedColumns
//                       )
//                 }
//                 pagination={!isTableFullScreen}
//                 fixedHeader={true}
//                 fixedHeaderScrollHeight={
//                   isTableFullScreen ? "calc(100vh - 180px)" : "460px"
//                 }
//                 responsive
//                 striped
//                 highlightOnHover
//                 paginationPerPage={25}
//                 paginationRowsPerPageOptions={[10, 25, 50, 100]}
//                 onChangePage={handlePageChange}
//                 onChangeRowsPerPage={handleRowsPerPageChange}
//                 paginationDefaultPage={currentPage}
//                 customStyles={customStyles}
//                 noDataComponent={
//                   <div className="p-10 text-center text-gray-500">
//                     No university data found.{" "}
//                     {isEditMode
//                       ? "Only LoginID and Password fields can be edited."
//                       : activeTab === "finalizedUniversity"
//                       ? "Click 'Edit LoginID/Password' to modify credentials."
//                       : ""}
//                   </div>
//                 }
//               />
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* Finalize Modal */}
//       {isFinalizeModalOpen && !selectedStudent.is_finalised && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
//           <div className="bg-white p-6 shadow-xl rounded-lg w-full md:w-1/3 max-w-md">
//             <div className="flex justify-between items-center border-b pb-3 mb-4">
//               <h2 className="text-xl font-semibold text-gray-800">
//                 Assign Application Manager
//               </h2>
//               <button
//                 onClick={() => setIsFinalizeModalOpen(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <p className="mb-4 text-gray-600">
//               Select an Application Manager before finalizing the university
//               list:
//             </p>

//             <select
//               className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               value={selectedManager}
//               onChange={(e) => setSelectedManager(e.target.value)}
//             >
//               <option value="">-- Select Manager --</option>
//               {admins &&
//                 admins.map((admin, index) => (
//                   <option key={index} value={admin.admin_id}>
//                     {admin.username}
//                   </option>
//                 ))}
//             </select>

//             <div className="flex justify-end space-x-3">
//               <button
//                 className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 onClick={() => setIsFinalizeModalOpen(false)}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 onClick={handleAssignAndFinalize}
//               >
//                 Assign & Finalize
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ShortlistedTable;
